import type { SymbolDataStructure } from '@tsagko/label-contracts'
import { resolveValueSource } from '../internal/units'

/**
 * Code-128 helpers (sub-task 2a.10.6).
 *
 * Code-128 supports the full ASCII range (0–127) plus a handful of extended
 * codepoints via its FNC characters. For Phase 1 we keep the validator
 * intentionally narrow: the caller is expected to have already routed the
 * payload through `escapeZplText`, which strips the ZPL command chars (`^`,
 * `~`) and most C0 control characters (everything except CR/LF/TAB). All this
 * helper has to do is reject empty payloads so the buildCommands branch can
 * silently skip them.
 *
 * Returning a discriminated `{ ok }` result rather than throwing keeps the
 * call-site in `buildCommands` purely functional: it treats a `false` result
 * as a silent skip (lint warnings are emitted separately in sub-task
 * 2a.10.15). Subsequent symbology helpers (EAN-13, GS1-128) follow the same
 * shape, so this module also seeded the `src/barcodes/` directory.
 *
 * The `derivePayloadForCode128` helper was relocated here from
 * `build-commands.ts` in sub-task 2a.10.7 as part of the
 * `compileSymbol` extraction — it's tightly coupled to Code-128's permissive
 * Phase-1 semantics for non-`raw`/`url` data structures.
 */

export type Code128ValidationResult =
  | { ok: true; payload: string }
  | { ok: false; reason: 'empty' }

export function validateCode128Payload(payload: string): Code128ValidationResult {
  if (payload.length === 0) {
    return { ok: false, reason: 'empty' }
  }
  return { ok: true, payload }
}

/**
 * Convert a `SymbolDataStructure` into a single payload string for Code-128.
 *
 * The structure's `kind` is normally constrained by the symbology — Code-128
 * is "happy" with `raw` and `url`, while `gtin` and `gs1-ai` are semantically
 * better suited to EAN-13 / GS1-128 respectively. The lint surface
 * (sub-task 2a.10.15) is responsible for flagging the mismatch; this helper
 * stays permissive in Phase 1 so an authoring mistake doesn't blow up the
 * whole render:
 *
 *   - `raw`     → resolve the ValueSource verbatim.
 *   - `url`     → resolve the ValueSource verbatim (the URL string itself).
 *   - `gtin`    → resolve the ValueSource verbatim (Phase 1 best-effort;
 *                 lint will warn).
 *   - `gs1-ai`  → concatenate as `(ai)value(ai)value`. This is how Code-128
 *                 typically encodes GS1 element strings (Phase 1
 *                 best-effort; lint will warn).
 */
export function derivePayloadForCode128(
  structure: SymbolDataStructure,
  data: Record<string, unknown>
): string {
  switch (structure.kind) {
    case 'raw':
    case 'url':
    case 'gtin':
      return resolveValueSource(structure.value, data)
    case 'gs1-ai':
      return structure.elements
        .map(el => `(${el.ai})${resolveValueSource(el.value, data)}`)
        .join('')
    default: {
      const _exhaustive: never = structure
      throw new Error(
        `derivePayloadForCode128: unknown structure ${JSON.stringify(_exhaustive)}`
      )
    }
  }
}
